import type { UseQueryResult } from '@tanstack/react-query';
import type { TestInstanceResponseWithResponseItemsAndMediaObjects } from 'services/assist-api/entities/test-instance-response/types';
import type { BaseTestInstance } from 'services/assist-api/entities/test-instance/types';
import {
  type TestItemWithRubricItems,
  useTestItems
} from 'services/assist-api/entities/test-item/test-items';

export default function useTestItemsForMarking(
  assessment: BaseTestInstance | undefined,
  response: TestInstanceResponseWithResponseItemsAndMediaObjects | undefined
): UseQueryResult<RequestResponse<TestItemWithRubricItems[]>, RequestError> {
  /**
   * Currently we are only fetching test items with a type 'arb-long-answer'.
   * This is because for CIPS writing, these are the only question types with
   * test rubric items and other test items such as those in the
   * "CIPS Writing - Reflection" category should not be marked or displayed.
   *
   * We should also filter to only return test items for the response items that exist on the
   * response. This is because, if there is a test item but not a corresponding
   * response item (for any reason), it is not possible to mark that item and it won't be possible
   * to submit the mark.
   */
  const uniqueTestItemIdsWithResponses = [
    ...new Set(response?.responseItems.map((ri) => ri.testItem.id))
  ];
  return useTestItems<TestItemWithRubricItems>(
    {
      filters: {
        id: uniqueTestItemIdsWithResponses,
        test: assessment?.test.id,
        type: 'arb-long-answer'
      },
      groups: ['testitem-testrubricitems'],
      include: 'testRubricItems'
    },
    { enabled: !!uniqueTestItemIdsWithResponses.length && !!assessment && !!response }
  );
}
